/**
 * COMMERCE-WORKSPACE-000002 — Orders founder workspace.
 * SoT: MarketSync domain=orders. Header-only until Order Items domain.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  amazonConnectApi,
  type OrderPage as OrderPageResult,
  type OrderRow,
  type SellerConnection,
} from '../../api/amazonconnect';
import {
  PAGE_SIZE_OPTIONS,
  PlatformDataGrid,
  type DataGridColumn,
  type DataGridMeta,
  type PageSizeOption,
} from '../../components/platform/PlatformDataGrid';
import { getMembershipId, getWorkspaceId } from '../../lib/auth-storage';
import { FounderCommerceShell } from './workspace/FounderCommerceShell';
import {
  ComingSoonCell,
  OrderItemsLink,
  OrderLink,
  SkuLink,
  WorkspaceLegend,
} from './workspace/commerceFields';
import { AsinLink } from './workspace/commerceFields';
import { useCommerceDomainHealth } from './workspace/useCommerceDomainHealth';

const ORDER_STATUSES = [
  '',
  'Pending',
  'Unshipped',
  'PartiallyShipped',
  'Shipped',
  'Canceled',
  'Unfulfillable',
];

export function MarketSyncOrdersPage() {
  const workspaceKey = getWorkspaceId() ?? 'default';
  const userKey = getMembershipId() ?? 'anon';
  const [params] = useSearchParams();

  const [connections, setConnections] = useState<SellerConnection[]>([]);
  const [connectionId, setConnectionId] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<PageSizeOption>(25);
  const [q, setQ] = useState('');
  const [qDraft, setQDraft] = useState('');
  const [sortCol, setSortCol] = useState('purchase_date');
  const [order, setOrder] = useState<'asc' | 'desc'>('desc');
  const [statusFilter, setStatusFilter] = useState('');
  const [result, setResult] = useState<OrderPageResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [latencyMs, setLatencyMs] = useState<number | undefined>();
  const [realtime, setRealtime] = useState(false);
  const [bootstrapped, setBootstrapped] = useState(false);

  useEffect(() => {
    void amazonConnectApi
      .listConnections()
      .then((c) => {
        setConnections(c);
        const urlConn = params.get('connection') || '';
        const pick = urlConn && c.some((x) => x.id === urlConn) ? urlConn : c[0]?.id || '';
        setConnectionId((prev) => prev || pick);
      })
      .catch(() => setConnections([]));
  }, [params]);

  useEffect(() => {
    if (bootstrapped) return;
    const uq = params.get('q') || '';
    if (uq) {
      setQ(uq);
      setQDraft(uq);
    }
    setBootstrapped(true);
  }, [params, bootstrapped]);

  const connection = connections.find((c) => c.id === connectionId);
  const health = useCommerceDomainHealth(connectionId, 'orders');
  const sortParam = order === 'desc' ? `${sortCol}_desc` : sortCol;

  const load = useCallback(async () => {
    if (!connectionId) {
      setLoading(false);
      setResult(null);
      return;
    }
    setLoading(true);
    setError(null);
    const t0 = performance.now();
    try {
      const res = await amazonConnectApi.listOrdersPage(connectionId, {
        page,
        page_size: pageSize,
        q: q || undefined,
        sort: sortParam,
        order,
        status: statusFilter || undefined,
      });
      setLatencyMs(Math.round(performance.now() - t0));
      setResult(res);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load orders');
      setResult(null);
      setLatencyMs(undefined);
    } finally {
      setLoading(false);
    }
  }, [connectionId, page, pageSize, q, sortParam, order, statusFilter]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!connectionId) return;
    setRealtime(true);
    void amazonConnectApi
      .commerceRealtimeRefresh(connectionId, ['orders'])
      .catch(() => undefined)
      .finally(() => {
        window.setTimeout(() => setRealtime(false), 4000);
      });
  }, [connectionId]);

  const rows = result?.items ?? [];
  const meta: DataGridMeta | null = result
    ? {
        total_count: result.total_count,
        returned_count: result.returned_count ?? result.count,
        page: result.page,
        page_size: result.page_size,
        pages: result.pages,
        remaining: result.remaining,
        has_more: result.has_more,
        execution_time_ms: result.execution_time_ms ?? latencyMs,
        source: result.source,
        data_source_badge: result.source || 'MarketSync · orders',
        last_sync: health.lastSync || connection?.last_sync_at || rows[0]?.last_sync,
        amazon_count: health.amazonCount,
        realtime: realtime || health.realtimeEligible,
      }
    : null;

  const columns: DataGridColumn<OrderRow>[] = useMemo(
    () => [
      {
        id: 'amazon_order_id',
        header: 'Amazon Order ID',
        width: 160,
        sortable: true,
        stickyLeft: 0,
        render: (r) => <OrderLink orderId={r.amazon_order_id} connectionId={connectionId} />,
        exportValue: (r) => r.amazon_order_id,
      },
      {
        id: 'purchase_date',
        header: 'Purchase (IST)',
        width: 150,
        sortable: true,
        render: (r) => (
          <span className="font-mono text-[10px]" title={r.purchase_date ? `UTC ${r.purchase_date}` : undefined}>
            {r.purchase_date_ist ?? r.purchase_date ?? '—'}
          </span>
        ),
        exportValue: (r) => r.purchase_date_ist ?? r.purchase_date ?? '',
      },
      {
        id: 'status',
        header: 'Status',
        width: 120,
        sortable: true,
        render: (r) => (
          <span className="rounded-md bg-slate-100 px-1.5 py-0.5 text-[11px] font-medium dark:bg-slate-800">
            {r.order_status ?? '—'}
          </span>
        ),
        exportValue: (r) => r.order_status ?? '',
      },
      {
        id: 'order_items',
        header: 'Items',
        width: 70,
        render: (r) => <OrderItemsLink orderId={r.amazon_order_id} connectionId={connectionId} />,
        exportValue: (r) => r.amazon_order_id,
      },
      {
        id: 'asin',
        header: 'ASIN',
        width: 110,
        render: (r) =>
          r.asin ? (
            <AsinLink asin={r.asin} marketplaceId={r.marketplace_id} connectionId={connectionId} />
          ) : (
            <ComingSoonCell />
          ),
        exportValue: (r) => r.asin ?? '',
      },
      {
        id: 'sku',
        header: 'SKU',
        width: 110,
        render: (r) =>
          r.sku ? <SkuLink sku={r.sku} connectionId={connectionId} /> : <ComingSoonCell />,
        exportValue: (r) => r.sku ?? '',
      },
      {
        id: 'quantity',
        header: 'Qty (header)',
        width: 90,
        render: (r) => (
          <span
            className="font-mono tabular-nums"
            title="Header quantity = items_shipped + items_unshipped (LOGIC-BIBLE). Line ASIN/SKU require getOrderItems."
          >
            {r.quantity}
          </span>
        ),
        exportValue: (r) => String(r.quantity),
      },
      {
        id: 'order_total',
        header: 'Total',
        width: 100,
        render: (r) =>
          r.order_total ? (
            <span className="font-mono tabular-nums">{r.order_total}</span>
          ) : (
            <ComingSoonCell />
          ),
        exportValue: (r) => r.order_total ?? '',
      },
      {
        id: 'currency',
        header: 'CCY',
        width: 56,
        render: (r) => r.currency ?? '—',
        exportValue: (r) => r.currency ?? '',
      },
      {
        id: 'fulfilment',
        header: 'Fulfilment',
        width: 90,
        render: (r) => r.fulfilment ?? '—',
        exportValue: (r) => r.fulfilment ?? '',
      },
      {
        id: 'marketplace',
        header: 'Marketplace',
        width: 110,
        render: (r) => <span className="font-mono">{r.marketplace_id ?? '—'}</span>,
        exportValue: (r) => r.marketplace_id ?? '',
      },
    ],
    [connectionId],
  );

  return (
    <FounderCommerceShell
      domain="orders"
      title="Orders"
      subtitle="Order headers from MarketSync. ASIN/SKU joined from Order Items. Times shown in IST (hover for UTC)."
      entryKey="Order ID"
      connection={connection}
      domainBadge={health.quality || 'orders'}
      legend={<WorkspaceLegend />}
    >
      <PlatformDataGrid
        gridId="orders-v2"
        workspaceKey={workspaceKey}
        userKey={userKey}
        columns={columns}
        rows={rows}
        rowKey={(r) => r.amazon_order_id}
        meta={meta}
        loading={loading}
        error={error}
        emptyMessage="No orders. Run Orders sync from Connect, then refresh."
        emptyAction={
          <Link to="/amazon/connect" className="rounded-lg bg-slate-900 px-4 py-2 text-sm text-white">
            Open Connect
          </Link>
        }
        page={page}
        pageSize={pageSize}
        sort={sortCol}
        order={order}
        searchDraft={qDraft}
        searchApplied={q || undefined}
        onSearchDraftChange={setQDraft}
        onSearch={() => {
          setQ(qDraft.trim());
          setPage(1);
        }}
        onPageChange={setPage}
        onPageSizeChange={(n) => {
          if (PAGE_SIZE_OPTIONS.includes(n)) {
            setPageSize(n);
            setPage(1);
          }
        }}
        onSortChange={(col, ord) => {
          setSortCol(col);
          setOrder(ord);
          setPage(1);
        }}
        onRefresh={() => void load()}
        selectable
        selectedKeys={selected}
        onSelectionChange={setSelected}
        density="compact"
        toolbarExtra={
          <>
            <label className="text-sm">
              <span className="text-xs font-medium text-slate-500">Connection</span>
              <select
                className="mt-1 block min-h-11 min-w-[16rem] rounded-lg border border-slate-300 px-2 dark:border-slate-600 dark:bg-slate-900"
                value={connectionId}
                onChange={(e) => {
                  setConnectionId(e.target.value);
                  setPage(1);
                  setSelected(new Set());
                }}
              >
                {connections.length === 0 && <option value="">No connections</option>}
                {connections.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.seller_ref || c.id.slice(0, 8)} · {c.status}
                  </option>
                ))}
              </select>
            </label>
            <label className="text-sm">
              <span className="text-xs font-medium text-slate-500">Status</span>
              <select
                className="mt-1 block min-h-11 min-w-[10rem] rounded-lg border border-slate-300 px-2 text-sm dark:border-slate-600 dark:bg-slate-900"
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setPage(1);
                }}
              >
                {ORDER_STATUSES.map((s) => (
                  <option key={s || 'all'} value={s}>
                    {s || 'All statuses'}
                  </option>
                ))}
              </select>
            </label>
          </>
        }
      />
    </FounderCommerceShell>
  );
}
